/**
 * snapshotAdapter — the cached rows, shape-shifted into the one snapshot every view is derived
 * from.
 *
 * SPLIT OUT OF `useContractSetupState` SO IT CAN BE TESTED, on the same reasoning as
 * `data/depositShapes.ts`: vitest runs node-env over `.ts` with no DOM and no browser storage, so
 * a module that reaches the supabase client cannot be imported by a test at all — and the
 * mapping below is precisely the layer that had nothing checking it. `LoanShape.rate` was read
 * for a column spelled `interest_rate` and the manifest reported every financed premium as
 * rateless; nothing failed, because a field that does not exist on an untyped row is `undefined`
 * rather than an error. The rules in `setupState.ts` were unit-tested from the first commit; the
 * adapter that feeds them was the only unchecked seam, and this file closes it.
 *
 * PURE, AND REACT-FREE. Every import here is a type except `firstOf` and `driverProfileOf`.
 */

import type { BoardSlot } from '@/components/agreements/boardSlots';
import type { Tables } from '@/integrations/supabase/types';
import type { AgreementTokenRow } from '@/lib/agreements/api';
import { driverProfileOf } from '@/lib/driverProfile';

import { firstOf, type ContractDeposit, type Embedded } from './data/depositShapes';
import type { ContractSetupStatus } from './data/contract';
import type {
    AgreementDocTypeKey,
    AgreementFact,
    SetupSnapshot,
    WitnessFacts,
} from './setupState';

/**
 * The cached rows as this adapter reads them.
 *
 * Structural, and only as wide as the snapshot needs: the generated row types describe the
 * table, not the nested select these queries make, and a shape declared here is what the
 * compiler checks the mapping below against.
 */
export interface ProfileShape {
    /**
     * NULL ON THE DRIVER'S OWN ROW. `profiles.driver_id` is unique only WHERE `admin_id IS NULL`,
     * so this embed may carry an admin's test profile beside the driver's — see `driverProfileOf`,
     * which is what picks between them below.
     */
    admin_id?: number | null;
    id?: number | null;
    first_name?: string | null;
    last_name?: string | null;
    phone?: string | null;
    address?: string | null;
    email?: string | null;
    birth_date?: string | null;
    photo?: string | null;
}

export interface DriverShape {
    id?: number | null;
    repayment_tag?: string | null;
    profiles?: Embedded<ProfileShape>;
    driver_identification?: Array<{ type?: string | null }> | null;
}

export interface VehicleShape {
    id?: number | null;
    make?: string | null;
    model?: string | null;
    /**
     * `vehicles.partner_id` — NOT NULL in SQL and set on all 289 vehicles, so a real read always
     * carries a number [D-B48].
     *
     * Nullable here for the reason `overdue_interest_rate` is on the contract shape: a select that
     * forgets to ask for it hands back `undefined` rather than an owner, and the snapshot's own
     * field is nullable so that absence resolves to "no third party" rather than to a crash. The
     * conservative direction is the other one — see `VehicleFacts.partnerId` — which is why the
     * adapter's mapping is the place the two are reconciled and the select above it is the place
     * the column is guaranteed.
     */
    partner_id?: number | null;
    vehicle_registrations?: Embedded<{ number?: string | null }>;
}

export interface ContractShape {
    driver_id?: number | null;
    vehicle_id?: number | null;
    vehicle_mileage?: number | null;
    sold_at?: number | null;
    /**
     * `contracts.cash_price` — the Schedule's first money row, owner-supplied and NEVER derived
     * from `sold_at`. Nullable for the same two reasons `overdue_interest_rate` is: the column
     * itself is nullable while a price goes unrecorded, and a select that forgets to ask for it
     * hands back `undefined` rather than a figure.
     */
    cash_price?: number | null;
    /**
     * `contracts.overdue_interest_rate` — NOT NULL (`20260801080000`) with DEFAULT 365
     * (`20260822010000`), so a real read always carries a number. Nullable here because the
     * generated types still describe the pre-migration column, and because a select that forgets
     * to ask for it (which is what this whole field is a fix for) hands back `undefined` rather
     * than a rate.
     */
    overdue_interest_rate?: number | null;
    drivers?: Embedded<DriverShape>;
    vehicles?: Embedded<VehicleShape>;
}

interface MoneyEmbed {
    amount?: number | null;
    transaction_at?: string | null;
}

interface PremiumShape {
    transactions?: Embedded<MoneyEmbed>;
    insurance_premium_discounts?: Array<{ transactions?: Embedded<MoneyEmbed> }> | null;
}

export interface PolicyShape {
    starts_at?: string | null;
    valued_at?: number | null;
    insurance_premiums?: PremiumShape[] | null;
}

/**
 * One `insurance_loans` row, as the snapshot needs it.
 *
 * THE COLUMN IS `interest_rate`. It is not `rate`, it has never been `rate`, and reading `rate`
 * off the untyped envelope is what put "financed, no rate" on the manifest for every contract
 * with an insurance loan. TAKEN FROM THE GENERATED ROW RATHER THAN RESTATED, because a restated
 * shape whose fields are all optional is a WEAK TYPE — TypeScript accepts any object sharing one
 * property with it, so `{ amount, rate }` would have compiled just as happily as the truth. A
 * `Pick` of the real row makes both names required and both mandatory to spell correctly.
 */
export type LoanShape = Pick<Tables<'insurance_loans'>, 'amount' | 'interest_rate'> & {
    /**
     * The loan's `insurance_loan_instalments` embed — its RATE HISTORY, which the advance's four
     * printed figures are derived from.
     *
     * `data/insurance.ts` has selected it since the envelope was written (`insurance_loans(*,
     * insurance_loan_instalments(*))`) and typed it on `ContractInsuranceLoan`; it was this shape
     * that never carried it through, so the snapshot's loan reached `advanceRepayments` with no
     * schedule at all and the dialog counted every advance's weekly repayment, first repayment
     * date and term as unfilled.
     *
     * OPTIONAL, BECAUSE AN EMBED A SELECT DID NOT ASK FOR IS ABSENT RATHER THAN EMPTY — and the
     * mapping below resolves that to `[]`. The weak-type hazard the docstring above warns about
     * does not apply: the `Pick` half keeps two properties REQUIRED, so an object that shares only
     * this one with the type is still rejected. `Pick`ed from the generated instalments row for
     * the same reason `interest_rate` is: `starts_at` is the column, and a restated `startsAt`
     * would read `undefined` off every row without failing anything.
     */
    insurance_loan_instalments?: Pick<
        Tables<'insurance_loan_instalments'>,
        'amount' | 'starts_at'
    >[] | null;
    /**
     * The loan's `insurance_loan_deposits` embed — WHAT THE DRIVER HAS PAID AGAINST THE PREMIUM
     * [D-B45], and one of the two limbs the instrument is derived from.
     *
     * THE MONEY IS ON THE TRANSACTION AND NEVER ON THE LINK ROW: `insurance_loan_deposits` carries
     * `loan_id` and `transaction_id` and nothing else, so a deposit whose transaction the embed did
     * not return contributes nothing to either figure. That reading is the conservative one — a
     * total that falls short names the ADVANCE, and only a total that COVERS the premium names the
     * payment record.
     *
     * OPTIONAL FOR `insurance_loan_instalments`' REASON: an embed a select did not ask for is
     * absent rather than empty, and the mapping below resolves that to `[]` — which is a deposit
     * total of 0, which is the advance.
     */
    insurance_loan_deposits?: LoanDepositShape[] | null;
};

/**
 * One deposit row as the envelope hands it back — the transaction accepted as an object OR as a
 * one-element array.
 *
 * PostgREST returns a to-one embed as an object and a to-many as an array, and which one arrives is
 * decided by the REQUEST rather than by the schema. `firstOf` below already accepts both readings
 * everywhere else in this adapter; the deposit's transaction is spelled the same way.
 */
interface LoanDepositShape {
    transactions?: Embedded<MoneyEmbed> | null;
}

export interface InsuranceEnvelopeShape {
    junctionId: number;
    policy?: PolicyShape | null;
    loans?: LoanShape[] | null;
}

export interface InstalmentShape {
    amount?: number | null;
    starts_at?: string | null;
}

export interface PrepItemShape {
    item: string;
    is_complete: boolean;
    details?: unknown;
}

export interface SnapshotInput {
    contractId: number;
    /** The latest `contract_updates` status, or null while the read is in flight. */
    status: string | null;
    /** The clock, fixed by the caller so the snapshot stays pure. */
    now: Date;
    prep: PrepItemShape[];
    contract: ContractShape | null;
    /** The insurance envelope — `junctionId`, `policy`, `loans`. Null when no policy is linked. */
    insurance: InsuranceEnvelopeShape | null;
    instalment: InstalmentShape | null;
    deposits: ContractDeposit[];
    witness: WitnessFacts | null;
    slots: BoardSlot[];
    /**
     * `useContractAgreements.failed` — any of the board's three reads was refused [D-B57].
     *
     * Carried rather than inferred from an empty board: the board is not empty when a read fails,
     * it is complete and partly derived over nothing. See `SetupSnapshot.agreementsUnread`.
     */
    agreementsFailed?: boolean;
    tokens: AgreementTokenRow[] | undefined;
    /** `missing[]` for a document whose render a row has already put in the cache. */
    renderMissing: (documentId: number) => string[] | null;
    drafts?: SetupSnapshot['drafts'];
    refusals?: string[];
    /**
     * `get_contract_setup_status` — the server's readiness answer, passed straight through.
     *
     * NOT RESHAPED HERE, and that is deliberate. Every other field on this input is a cached
     * PostgREST row being bent into the snapshot's vocabulary; this one is already the answer, and
     * the value of it is that it arrives in the server's words. A mapping layer over it would be a
     * second place for the codes to drift from the ones `sign_contract` will refuse in.
     */
    setupStatus?: ContractSetupStatus | null;
}

/** The completion date the manifest prints beside a preparation item, where the RPC carries one. */
export function prepCompletedAt(item: string, details: unknown): string | null {
    if (!details) return null;
    if (item === 'service') {
        const entries = Array.isArray(details) ? (details as Array<{ ended_at?: string | null }>) : [];
        const ended = entries
            .map((entry) => entry?.ended_at)
            .filter((value): value is string => !!value)
            .sort();
        return ended.length ? ended[ended.length - 1] : null;
    }
    if (item === 'insurance') return (details as { starts_at?: string | null }).starts_at ?? null;
    return null;
}

/**
 * WHAT THE DRIVER HAS PAID AGAINST THIS PREMIUM — Σ of the TRANSACTIONS behind the deposits.
 *
 * The same sum `total_paid` takes (`20260823010000…sql:958-982`: join the deposits to
 * `transactions` and add `t.amount`), and the same one `agreement_create` compares the premium
 * against. A row whose transaction the embed did not return adds nothing, which keeps a partial
 * read on the ADVANCE side of the comparison rather than the payment record's.
 */
function loanDepositTotal(rows: LoanDepositShape[] | null | undefined): number {
    return (rows ?? []).reduce((total, row) => total + (firstOf(row.transactions)?.amount ?? 0), 0);
}

/**
 * WHEN THE PREMIUM WAS PAID — the LATEST deposit transaction, still as the raw TIMESTAMPTZ.
 *
 * THE LATEST AND NOT THE FIRST, because a premium can be paid in two goes: loan 294 carries two
 * deposit transactions, and the day the payment was COMPLETED is the day the Record states it was
 * paid. `insurance.premium_paid_on` resolves the same row on the server.
 *
 * IT HANDS BACK THE INSTANT, NEVER A CALENDAR DAY. `transaction_at` is a TIMESTAMPTZ and the Ghana
 * business day is a display concern — every printer of this value goes through `dateText` /
 * `businessDay`, so converting here would date the payment twice. The comparison is on the parsed
 * epoch rather than on the string, so a row that came back in another offset still sorts correctly.
 */
function latestDepositDate(rows: LoanDepositShape[] | null | undefined): string | null {
    let latest: string | null = null;
    let latestAt = Number.NEGATIVE_INFINITY;
    for (const row of rows ?? []) {
        const paidAt = firstOf(row.transactions)?.transaction_at ?? null;
        if (!paidAt) continue;
        const instant = Date.parse(paidAt);
        if (!Number.isFinite(instant)) continue;
        // `>=`: two payments recorded at the same instant are indeterminate, and the row read last
        // wins — the same tie-break `advanceRepayments` takes over a shared instalment start.
        if (instant >= latestAt) {
            latestAt = instant;
            latest = paidAt;
        }
    }
    return latest;
}

export function agreementFacts(
    slots: BoardSlot[],
    tokens: AgreementTokenRow[] | undefined,
    renderMissing: (documentId: number) => string[] | null,
): AgreementFact[] {
    return slots.map((slot) => {
        const document = slot.document;
        return {
            docType: slot.docType as AgreementDocTypeKey,
            subjectId: slot.subjectId,
            // WHICH OF THE TWO INSURANCE INSTRUMENTS THIS SLOT IS [D-B45], COPIED AND NOT
            // RE-DERIVED, for the reason the two cross-document facts below are copied: the board
            // derived it once from the loan's own rows, and a second derivation over the same rows
            // is a second answer that can disagree. The manifest needs it because it mirrors
            // D-B27's countersign conjunction, and the Payment Record drops that rule's witness arm.
            instrument: slot.instrument,
            legacySignedAt: slot.legacySignedAt,
            legacyUrl: slot.legacyUrl,
            // THE TWO CROSS-DOCUMENT FACTS [D-B18], COPIED AND NOT RECOMPUTED. `buildBoardSlots`
            // derived them once from the rows `agreement_sign` reads; carrying them is what keeps
            // the manifest and the signing board reading one derivation rather than two.
            driverSigned: slot.driverSigned,
            witnessSigned: slot.witnessSigned,
            // AND THE FOURTH ONE TRAVELS WITH THEM [D-B53]: whether this contract's hire purchase
            // agreement came back on paper. The manifest mirrors D-B27's countersign conjunction and
            // the paper master drops that rule's witness arm on both advances, so the note has to
            // read the board's own conjunction rather than run a second one over the same two rows.
            masterOnPaper: slot.masterOnPaper,
            document: document
                ? {
                    id: document.id,
                    status: document.status,
                    finalizedAt: document.finalized_at,
                    missing: renderMissing(document.id),
                    signatures: [...document.signatures]
                        .sort((a, b) => (a.sign_order ?? 0) - (b.sign_order ?? 0))
                        .map((signature) => ({
                            party: signature.party,
                            status: signature.status,
                            signedAt: signature.signed_at,
                        })),
                }
                : null,
            tokens: (tokens ?? [])
                .filter((token) => document && token.document_id === document.id && !token.revoked_at)
                .map((token) => ({
                    party: token.party,
                    // The ledger records when the link was MINTED, and a link is shared the moment
                    // it is minted — there is no separate send. `use_count` is the open count the
                    // driver app increments through `agreement_token_lookup`.
                    sentAt: token.created_at,
                    lastOpenedAt: null,
                    openCount: token.use_count ?? 0,
                })),
        };
    });
}

/** The whole snapshot, from the cached rows. One function, so one place can be wrong. */
export function buildSetupSnapshot(input: SnapshotInput): SetupSnapshot {
    const contract = input.contract ?? {};
    const driverRow = firstOf(contract.drivers);
    // NOT `firstOf`. The `drivers → profiles` embed is the ONE embed on this page that is not
    // one-per-row: an admin may hold a test profile against the same driver id, and reading the
    // array by position put the ADMIN's name and the ADMIN's date of birth into the snapshot every
    // surface on this page derives from — while `get_contract_setup_status`, which excludes admin
    // rows in SQL, went on refusing the activation over the driver's own empty fields.
    const profile = driverProfileOf(driverRow?.profiles);
    const identification = driverRow?.driver_identification ?? [];
    const vehicle = firstOf(contract.vehicles);
    const registration = firstOf(vehicle?.vehicle_registrations);
    const policy = input.insurance?.policy ?? null;
    const premium = firstOf(policy?.insurance_premiums);
    const premiumTransaction = firstOf(premium?.transactions);
    const discount = firstOf(premium?.insurance_premium_discounts);
    const loan = firstOf(input.insurance?.loans ?? []);

    return {
        contractId: input.contractId,
        status: input.status ?? 'DRAFT',
        now: input.now,
        prep: input.prep.map((item) => ({
            item: item.item,
            is_complete: item.is_complete,
            completedAt: prepCompletedAt(item.item, item.details),
        })),
        vehicle: {
            id: (vehicle?.id ?? contract.vehicle_id ?? null) as number | null,
            make: vehicle?.make ?? null,
            model: vehicle?.model ?? null,
            registration: registration?.number ?? null,
            mileage: contract.vehicle_mileage ?? null,
            // THE VEHICLE'S OWNER, AND THE WHOLE OF WHAT DECIDES WHETHER THIS CONTRACT OWES A
            // VEHICLE SCHEDULE [D-B48]. Read off the embed rather than off a partner row, because
            // the id is all the mirror needs (`isThirdPartyVehicle`) and reaching `partners` for
            // `is_house` would fail this select with 42703 wherever the migration has not landed —
            // taking the whole Dossier down for a grey mark.
            partnerId: vehicle?.partner_id ?? null,
        },
        driver: {
            id: (driverRow?.id ?? contract.driver_id ?? null) as number | null,
            // THE HALVES BEFORE THE JOIN. `fullName` is what the page prints; these two are what
            // the server grades one at a time, and the refusal sentence names the one that is
            // absent — see `driverProfileGapSentence`.
            firstName: profile?.first_name ?? null,
            lastName: profile?.last_name ?? null,
            fullName:
                [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim() || null,
            phone: profile?.phone ?? null,
            address: profile?.address ?? null,
            email: profile?.email ?? null,
            birthDate: profile?.birth_date ?? null,
            photo: profile?.photo ?? null,
            repaymentTag: driverRow?.repayment_tag ?? null,
            licenceOnFile: identification.some((row) => row?.type === 'DRIVING_LICENCE'),
            nationalIdOnFile: identification.some((row) => row?.type === 'NATIONAL_ID'),
        },
        witness: input.witness,
        deposits: input.deposits.map((deposit) => ({
            id: deposit.id,
            amount: deposit.amount,
            paidOn: deposit.transaction_at,
        })),
        insurance: input.insurance
            ? {
                junctionId: input.insurance.junctionId,
                policyStartsAt: policy?.starts_at ?? null,
                policyValuedAt: policy?.valued_at ?? null,
                premium: premiumTransaction
                    ? { amount: premiumTransaction.amount ?? null, paidOn: premiumTransaction.transaction_at ?? null }
                    : null,
                discount: discount ? { amount: firstOf(discount.transactions)?.amount ?? null } : null,
                // `interest_rate` is the column; `rate` is the snapshot's own vocabulary. The
                // instalments are carried in the READ ORDER the envelope fetched them in
                // (`insurance_loan_instalments.id ASC`) and are NOT sorted here: they are a rate
                // history, and both questions asked of them — the LATEST rate and the EARLIEST
                // start — are answered over `starts_at` by the derivation that reads them.
                loan: loan
                    ? {
                        amount: loan.amount ?? null,
                        rate: loan.interest_rate ?? null,
                        instalments: (loan.insurance_loan_instalments ?? []).map((row) => ({
                            amount: row.amount ?? null,
                            startsAt: row.starts_at ?? null,
                        })),
                        // THE DRIVER'S OWN PAYMENTS AGAINST THE PREMIUM [D-B45], summed and
                        // dated here so no surface downstream walks the embed twice. NOTHING IS
                        // ROUNDED: the half-pesewa tolerance lives on the comparison in
                        // `instrumentFor`, which is where the server puts it too.
                        depositTotal: loanDepositTotal(loan.insurance_loan_deposits),
                        depositPaidOn: latestDepositDate(loan.insurance_loan_deposits),
                    }
                    : null,
            }
            : null,
        financials: {
            cashPrice: contract.cash_price ?? null,
            sellingPrice: contract.sold_at ?? null,
            instalment: input.instalment
                ? { amount: input.instalment.amount ?? null, startsAt: input.instalment.starts_at ?? null }
                : null,
            overdueRate: contract.overdue_interest_rate ?? null,
        },
        agreements: agreementFacts(input.slots, input.tokens, input.renderMissing),
        agreementsUnread: input.agreementsFailed ?? false,
        drafts: input.drafts,
        refusals: input.refusals,
        serverStatus: input.setupStatus ?? null,
    };
}
